import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    TreePine, 
    Clock, 
    Users, 
    CheckCircle2, 
    CalendarDays,
    ShoppingCart,
    Loader2,
    Star,
    Heart,
    HelpCircle,
    ArrowRight,
    Mail,
    Info,
    Phone,
    UserPlus,
    AlertCircle,
    X,
    Wifi,
    WifiOff,
    MapPin
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { useRealtimeUpdates } from '../hooks/useRealtimeUpdates';
import SEOHead, { pageSEO, generateServiceSchema, generateBreadcrumbSchema } from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MantrailingPage = () => {
    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [addingToCart, setAddingToCart] = useState(false);
    const [availableDates, setAvailableDates] = useState([]);
    const [blockedDates, setBlockedDates] = useState([]);
    
    // Waitlist states
    const [showWaitlistModal, setShowWaitlistModal] = useState(false);
    const [waitlistSlot, setWaitlistSlot] = useState(null);
    const [waitlistForm, setWaitlistForm] = useState({ name: '', email: '', phone: '' });
    const [submittingWaitlist, setSubmittingWaitlist] = useState(false);

    const { addToCart } = useCart();

    // Fetch functions defined with useCallback so they can be used in SSE handlers
    const fetchBlockedDates = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/blocked-dates`);
            const blocked = response.data.blocked_dates || [];
            setBlockedDates(blocked);
        } catch (error) {
        }
    }, []);

    const fetchAvailableDates = useCallback(async (sessionId) => {
        try {
            const response = await axios.get(`${API}/availability/${sessionId}`);
            const dates = [...new Set(response.data
                .filter(slot => !slot.is_blocked)
                .map(slot => slot.date))];
            setAvailableDates(dates);
        } catch (error) {
        }
    }, []);

    const fetchAvailableSlots = useCallback(async (sessionId, date) => {
        try {
            const formattedDate = format(date, 'yyyy-MM-dd');
            const response = await axios.get(`${API}/availability/${sessionId}?month=${formattedDate.substring(0, 7)}`);
            const slotsForDate = response.data.filter(slot => 
                slot.date === formattedDate && !slot.is_blocked
            );
            setAvailableSlots(slotsForDate);
            setSelectedSlot(null);
        } catch (error) {
            setAvailableSlots([]);
        }
    }, []);

    const fetchSessions = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/sessions`);
            setSessions(response.data);
            const bookableSessions = response.data.filter(s => s.category !== 'workshop');
            if (bookableSessions.length > 0) {
                setSelectedSession(bookableSessions[0]);
            }
        } catch (error) {
            toast.error('Failed to load sessions');
        } finally {
            setLoading(false);
        }
    }, []);

    // Real-time update handlers
    const handleBlockedDateAdded = useCallback((data) => {
        toast.info('Availability updated', { description: 'Some dates are no longer available' });
        fetchBlockedDates();
        if (selectedSession) {
            fetchAvailableDates(selectedSession.id);
        }
    }, [selectedSession, fetchBlockedDates, fetchAvailableDates]);

    const handleBlockedDateRemoved = useCallback((data) => {
        toast.success('New dates available!', { description: 'Some dates are now open for booking' });
        fetchBlockedDates();
        if (selectedSession) {
            fetchAvailableDates(selectedSession.id);
        }
    }, [selectedSession, fetchBlockedDates, fetchAvailableDates]);

    const handleSlotAvailabilityChanged = useCallback((data) => {
        if (selectedSession && data.session_id === selectedSession.id) {
            if (selectedDate) {
                fetchAvailableSlots(selectedSession.id, selectedDate);
            }
            fetchAvailableDates(selectedSession.id);
        }
    }, [selectedSession, selectedDate, fetchAvailableSlots, fetchAvailableDates]);

    const handleSlotsUpdate = useCallback((data) => {
        toast.info('New slots available!');
        if (selectedSession) {
            fetchAvailableDates(selectedSession.id);
            if (selectedDate) {
                fetchAvailableSlots(selectedSession.id, selectedDate);
            }
        }
    }, [selectedSession, selectedDate, fetchAvailableDates, fetchAvailableSlots]);

    // Subscribe to real-time updates
    useRealtimeUpdates({
        onBlockedDateAdded: handleBlockedDateAdded,
        onBlockedDateRemoved: handleBlockedDateRemoved,
        onSlotAvailabilityChanged: handleSlotAvailabilityChanged,
        onSlotAdded: handleSlotsUpdate,
        onSlotRemoved: handleSlotsUpdate,
        onSlotsBulkAdded: handleSlotsUpdate
    });

    useEffect(() => {
        fetchSessions();
        fetchBlockedDates();
    }, [fetchSessions, fetchBlockedDates]);

    useEffect(() => {
        if (selectedSession) {
            fetchAvailableDates(selectedSession.id);
        }
    }, [selectedSession, fetchAvailableDates]);

    useEffect(() => {
        if (selectedSession && selectedDate) {
            fetchAvailableSlots(selectedSession.id, selectedDate);
        }
    }, [selectedSession, selectedDate, fetchAvailableSlots]);

    const isDateBlocked = (date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        return blockedDates.includes(dateStr);
    };

    const handleSlotClick = (slot) => {
        if (slot.is_blocked) {
            toast.error('This date is not available for booking');
            return;
        }
        if (slot.is_full || slot.available_spots <= 0) {
            // Show waitlist modal for full slots
            setWaitlistSlot(slot);
            setShowWaitlistModal(true);
        } else {
            setSelectedSlot(slot);
        }
    };

    const handleWaitlistSubmit = async (e) => {
        e.preventDefault();
        
        if (!waitlistForm.name || !waitlistForm.email || !waitlistForm.phone) {
            toast.error('Please fill in all fields');
            return;
        }
        
        setSubmittingWaitlist(true);
        try {
            await axios.post(`${API}/waitlist`, {
                slot_id: waitlistSlot.id,
                name: waitlistForm.name,
                email: waitlistForm.email,
                phone: waitlistForm.phone
            });
            
            toast.success('Added to waitlist!', {
                description: "We'll contact you if a spot becomes available."
            });
            
            setShowWaitlistModal(false);
            setWaitlistForm({ name: '', email: '', phone: '' });
            setWaitlistSlot(null);
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to join waitlist';
            toast.error(message);
        } finally {
            setSubmittingWaitlist(false);
        }
    };

    const handleAddToCart = async () => {
        if (!selectedSession || !selectedSlot) {
            toast.error('Please select a session and time slot');
            return;
        }

        setAddingToCart(true);
        const result = await addToCart(selectedSession.id, selectedSlot.id);
        setAddingToCart(false);

        if (result.success) {
            toast.success('Added to cart!', {
                description: `${selectedSession.name} on ${format(selectedDate, 'dd MMM yyyy')} at ${selectedSlot.time}`
            });
            setSelectedSlot(null);
            if (selectedSession && selectedDate) {
                fetchAvailableSlots(selectedSession.id, selectedDate);
            }
        } else {
            toast.error(result.error || 'Failed to add to cart');
        }
    };

    const isDateAvailable = (date) => {
        const formattedDate = format(date, 'yyyy-MM-dd');
        return availableDates.includes(formattedDate);
    };

    const handleSessionSelect = (session) => {
        setSelectedSession(session);
        setSelectedDate(null);
        setSelectedSlot(null);
        setAvailableSlots([]);
    };

    // Filter bookable sessions (not workshops)
    const bookableSessions = sessions.filter(s => s.category !== 'workshop');

    // Questions for intro section
    const introQuestions = [
        "Have you ever fancied playing a game of hide and seek with your dog?",
        "Do you feel like sometimes a walk just simply isn't enough to tire your dog out?",
        "Looking for an activity you and your dog can enjoy together as a true team?",
        "Have a dog that's reactive, nervous, over-excitable—or just bursting with energy?",
        "Got a dog who lives for sniffing and following interesting smells?",
        "Love the idea of seeing your dog focused, fulfilled, and doing what they love most—sniffing?"
    ];

    // Schema markup for Mantrailing services
    const mantrailingSchemas = [
        generateServiceSchema({
            name: 'Mantrailing Introduction Workshop',
            description: 'A three-hour introduction workshop teaching the foundations of mantrailing. Perfect for beginners and dogs of all breeds near Malmesbury, Wiltshire.',
            price: '65.00'
        }),
        generateServiceSchema({
            name: 'Mantrailing Training Session',
            description: 'Progression mantrailing session for handlers who have completed an introduction workshop. Build confidence and skills in Wiltshire.',
            price: '30.00'
        }),
        generateBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Mantrailing', url: '/mantrailing' }
        ])
    ];

    return (
        <div className="min-h-screen" data-testid="mantrailing-page">
            <SEOHead 
                title={pageSEO.mantrailing.title}
                description={pageSEO.mantrailing.description}
                keywords={pageSEO.mantrailing.keywords}
                canonical={pageSEO.mantrailing.canonical}
                schema={mantrailingSchemas}
            />
            {/* Hero Section with Photo */}
            <section className="relative py-44 md:py-64" data-testid="mantrailing-hero">
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url('https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/5nx195fv_DSCF7164.JPG')`
                    }}
                >
                    <div className="absolute inset-0 bg-primary/75"></div>
                </div>
                
                <div className="container-custom relative z-10">
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-3 mb-6">
                            <img 
                                src="https://customer-assets.emergentagent.com/job_canine-training-pro/artifacts/k6jr7ouk_Laura%20logo%20CC.jpeg" 
                                alt="Craven Canines" 
                                className="h-14 w-auto bg-white rounded-lg p-1"
                            />
                            <span className="text-white/90 font-medium text-lg">Craven Canines</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight font-serif">
                            Mantrailing Sessions & Workshops in Wiltshire
                        </h1>
                        <p className="text-xl text-white/90 leading-relaxed">
                            A scent work sport for all dogs and their humans! Discover mantrailing 
                            near Malmesbury and watch your dog thrive with expert-led training.
                        </p>
                        <div className="mt-8">
                            <a 
                                href="#booking-section" 
                                className="inline-flex items-center gap-2 bg-white text-primary px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/90 transition-colors shadow-lg"
                                data-testid="hero-book-button"
                            >
                                Book a Session
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 1: What is Mantrailing */}
            <section className="section-spacing" id="what-is-mantrailing" data-testid="what-is-mantrailing">
                <div className="container-custom">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-10">
                            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
                                <TreePine className="w-4 h-4" /> What is Mantrailing?
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-foreground font-serif mb-4">
                                A scent work sport for all dogs and their humans!
                            </h2>
                        </div>
                        
                        <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
                            <div className="bg-secondary/50 rounded-2xl p-6 my-8">
                                <p className="text-lg font-semibold text-foreground">
                                    Mantrailing is simply a highly addictive, low-impact game of hide and seek with your dog.
                                </p>
                            </div>
                            
                            <p className="leading-relaxed">
                                Mantrailing is one of the fastest growing dog sports in the UK. It is a fun, scent-work activity 
                                where dogs tap into their incredible natural sense of smell to follow a specific human scent and 
                                locate a hidden person.
                            </p>
                            
                            <p className="leading-relaxed">
                                Every person carries a unique scent, created from skin cells, natural oils, and hormones. In 
                                Mantrailing, your dog learns to take that scent from a personal article, recognise it, and follow 
                                the trail left behind to locate the "missing" person and earn their reward.
                            </p>
                            
                            <p className="leading-relaxed">
                                Working alongside you as a team, your dog uses their natural abilities in a relaxed, low-pressure 
                                environment suitable for all ages, breeds, and abilities. Dogs work individually and remain on lead, 
                                allowing us to safely support reactive, nervous, or high-energy dogs.
                            </p>
                            
                            <p className="leading-relaxed">
                                Mantrailing provides fantastic mental stimulation, builds confidence, and creates a real sense of 
                                shared achievement — all while letting your dog do what they love most: sniff.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 2: Who is Mantrailing for */}
            <section className="section-spacing bg-primary/5" id="who-is-mantrailing-for" data-testid="who-section">
                <div className="container-custom">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
                            <Users className="w-4 h-4" /> Who is Mantrailing For?
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground font-serif mb-4">
                            Suitable for Almost Every Dog!
                        </h2>
                        <p className="text-muted-foreground">
                            The wonderful thing about Mantrailing is that it's suitable for almost every dog!
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                        <Card className="card-feature text-center">
                            <CardContent className="p-8">
                                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <TreePine className="w-8 h-8 text-primary" />
                                </div>
                                <h3 className="text-xl font-bold mb-2 font-serif">All Breeds</h3>
                                <p className="text-muted-foreground">
                                    From tiny Chihuahuas to giant Great Danes – every nose can trail!
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="card-feature text-center">
                            <CardContent className="p-8">
                                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Clock className="w-8 h-8 text-primary" />
                                </div>
                                <h3 className="text-xl font-bold mb-2 font-serif">All Ages</h3>
                                <p className="text-muted-foreground">
                                    Puppies to seniors can enjoy Mantrailing at their own pace
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="card-feature text-center">
                            <CardContent className="p-8">
                                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Heart className="w-8 h-8 text-primary" />
                                </div>
                                <h3 className="text-xl font-bold mb-2 font-serif">Nervous Dogs Welcome</h3>
                                <p className="text-muted-foreground">
                                    A perfect confidence-building activity for anxious or reactive dogs
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* SECTION 3: Is Mantrailing for You? (Questions) */}
            <section className="section-spacing bg-secondary/30" id="is-mantrailing-for-you" data-testid="intro-questions">
                <div className="container-custom">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-10">
                            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
                                <HelpCircle className="w-4 h-4" /> Is Mantrailing For You?
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-foreground font-serif mb-4">
                                See If It's Right For You
                            </h2>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                            {introQuestions.map((question, idx) => (
                                <div 
                                    key={`intro-q-${idx}`}
                                    className="flex items-start gap-4 p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                                >
                                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                        <CheckCircle2 className="w-5 h-5 text-primary" />
                                    </div>
                                    <p className="text-foreground font-medium leading-relaxed">{question}</p>
                                </div>
                            ))}
                        </div>
                        
                        <div className="mt-10 text-center">
                            <div className="bg-primary/10 rounded-2xl p-8">
                                <p className="text-xl md:text-2xl font-bold text-primary font-serif">
                                    If you answered "yes" to any of these questions, then give Mantrailing a go!
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 4: What We Offer */}
            <section className="section-spacing" id="what-we-offer" data-testid="what-we-offer">
                <div className="container-custom">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
                            <Star className="w-4 h-4" /> What We Offer
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground font-serif mb-4">
                            Mantrailing Sessions & Workshops
                        </h2>
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 text-left max-w-3xl mx-auto">
                            <MapPin className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-amber-800 text-sm">
                                Most session locations will be within a 20-minute radius of Malmesbury. However, when booking a session, please allow sufficient time either side of your appointment to accommodate travel. The exact location will be provided via email at least 24 hours prior to your scheduled session.
                            </p>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
                        {/* Introduction Workshop */}
                        <Card className="card-feature overflow-hidden">
                            <CardContent className="p-0">
                                <div className="bg-primary text-white p-4 sm:p-6">
                                    <div className="flex flex-wrap justify-between items-start gap-2">
                                        <div className="min-w-0">
                                            <Badge className="bg-white/20 text-white mb-2">Perfect Starting Point</Badge>
                                            <h3 className="text-xl sm:text-2xl font-bold font-serif">Introduction Workshop</h3>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <span className="text-2xl sm:text-3xl font-bold">£65</span>
                                            <p className="text-white/80 text-sm">3 hours</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-4 sm:p-6 space-y-4">
                                    <p className="text-muted-foreground">
                                        Your Mantrailing journey starts here. This three-hour introduction workshop is designed 
                                        to teach the foundations of Mantrailing, working one handler and dog at a time.
                                    </p>
                                    <p className="text-muted-foreground">
                                        You and your dog will complete several scent trails, each gradually increasing in length and difficulty.
                                    </p>
                                    
                                    <div className="bg-secondary/50 rounded-xl p-4">
                                        <h4 className="font-semibold text-foreground mb-3">During this session you will learn:</h4>
                                        <ul className="space-y-2">
                                            <li className="flex items-start gap-2 text-sm text-muted-foreground">
                                                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                                What the game of Mantrailing is all about
                                            </li>
                                            <li className="flex items-start gap-2 text-sm text-muted-foreground">
                                                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                                What scent work is and how to safely handle a long line
                                            </li>
                                            <li className="flex items-start gap-2 text-sm text-muted-foreground">
                                                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                                How to read your dog's body language and what to look out for
                                            </li>
                                        </ul>
                                    </div>
                                    
                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                        <h4 className="font-semibold text-foreground mb-2">Requirements:</h4>
                                        <ul className="space-y-1 text-sm text-muted-foreground">
                                            <li>• A suitable harness</li>
                                            <li>• A long line (10m)</li>
                                            <li>• Plenty of high value treats, or a toy (bring both if you are not sure)</li>
                                        </ul>
                                        <p className="text-xs text-amber-700 mt-3">
                                            <strong>Please note:</strong> Dogs will rest in your car between trails while another team hides. 
                                            If your dog is not comfortable being alone, please bring someone along to sit with them.
                                        </p>
                                    </div>
                                    
                                    <Button 
                                        className="w-full btn-primary"
                                        onClick={() => {
                                            const introSession = sessions.find(s => s.name.includes('Introduction'));
                                            if (introSession) handleSessionSelect(introSession);
                                            document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                    >
                                        Book Now <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Training Sessions (Progression) */}
                        <Card className="card-feature overflow-hidden">
                            <CardContent className="p-0">
                                <div className="bg-foreground text-white p-4 sm:p-6">
                                    <div className="flex flex-wrap justify-between items-start gap-2">
                                        <div className="min-w-0">
                                            <Badge className="bg-white/20 text-white mb-2">Progression Sessions</Badge>
                                            <h3 className="text-xl sm:text-2xl font-bold font-serif">Training Sessions</h3>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <span className="text-2xl sm:text-3xl font-bold">£30</span>
                                            <p className="text-white/80 text-sm">3 hours</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-4 sm:p-6 space-y-4">
                                    <p className="text-muted-foreground">
                                        Suitable for handlers who have already completed an Introduction Mantrailing Workshop 
                                        (with Craven Canines or another instructor).
                                    </p>
                                    <p className="text-muted-foreground">
                                        These sessions focus on building confidence, improving skills, and progressing your 
                                        Mantrailing journey in a relaxed and supportive environment.
                                    </p>
                                    
                                    {/* Session Duration Info */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                        <p className="text-blue-800 font-medium">
                                            Sessions are typically 3 hours in length.
                                        </p>
                                    </div>
                                    
                                    <div className="bg-secondary/50 rounded-xl p-4">
                                        <h4 className="font-semibold text-foreground mb-3">What to expect:</h4>
                                        <ul className="space-y-2">
                                            <li className="flex items-start gap-2 text-sm text-muted-foreground">
                                                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                                Build on your introduction workshop skills
                                            </li>
                                            <li className="flex items-start gap-2 text-sm text-muted-foreground">
                                                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                                Progress at your own pace
                                            </li>
                                            <li className="flex items-start gap-2 text-sm text-muted-foreground">
                                                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                                Various locations across Wiltshire
                                            </li>
                                        </ul>
                                    </div>
                                    
                                    <Button 
                                        className="w-full btn-primary"
                                        onClick={() => {
                                            const trainingSession = sessions.find(s => s.name.includes('Training Session'));
                                            if (trainingSession) handleSessionSelect(trainingSession);
                                            document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                    >
                                        Book Now <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Workshops - Contact to Book */}
                    <div className="mt-8 max-w-5xl mx-auto">
                        <Card className="card-feature overflow-hidden">
                            <CardContent className="p-0">
                                <div className="bg-accent text-white p-4 sm:p-6">
                                    <div className="text-center">
                                        <Badge className="bg-white/20 text-white mb-2">Immersive Experiences</Badge>
                                        <h3 className="text-xl sm:text-2xl font-bold font-serif">Mantrailing Workshops – Half Day & Full Day</h3>
                                        <p className="text-white/90 mt-2">
                                            Perfect for groups who want some serious Mantrailing mayhem with friends!
                                        </p>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="grid md:grid-cols-2 gap-6">
                                        {/* Full Day Workshop */}
                                        <div className="bg-secondary/30 rounded-xl p-6 text-center">
                                            <h4 className="text-xl font-bold text-foreground mb-2">Full Day Workshop</h4>
                                            <p className="text-muted-foreground text-sm mb-3">7 hours • Max 6 people</p>
                                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                                <p className="text-green-800 text-sm font-medium flex items-center justify-center gap-2">
                                                    <Info className="w-4 h-4" />
                                                    Price is split between attendees (maximum 6 participants)
                                                </p>
                                            </div>
                                        </div>
                                        {/* Half Day Workshop */}
                                        <div className="bg-secondary/30 rounded-xl p-6 text-center">
                                            <h4 className="text-xl font-bold text-foreground mb-2">Half Day Workshop</h4>
                                            <p className="text-muted-foreground text-sm mb-3">4.5 hours • Max 4 people</p>
                                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                                <p className="text-green-800 text-sm font-medium flex items-center justify-center gap-2">
                                                    <Info className="w-4 h-4" />
                                                    Price is split between attendees (maximum 4 participants)
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="mt-6 text-center">
                                        <p className="text-muted-foreground mb-4">
                                            Interested in booking a workshop for your group? Get in touch to discuss dates and availability.
                                        </p>
                                        <Link to="/contact">
                                            <Button className="btn-primary">
                                                <Mail className="w-4 h-4 mr-2" />
                                                Contact Me to Book
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* SECTION 5: Book a Session */}
            <section className="section-spacing bg-secondary/30" id="booking-section" data-testid="booking-section">
                <div className="container-custom">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
                            <CalendarDays className="w-4 h-4" /> Book Your Session
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground font-serif mb-4">
                            Choose Your Date & Time
                        </h2>
                        <p className="text-muted-foreground">
                            Select a session, pick your preferred date, and choose a time slot
                        </p>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
                            {/* Session Selection - Only bookable sessions */}
                            <div className="space-y-4" data-testid="session-list">
                                <h3 className="text-lg font-semibold text-foreground mb-4">1. Select Session</h3>
                                <div className="space-y-3">
                                    {bookableSessions.map((session) => (
                                        <Card 
                                            key={session.id}
                                            className={`cursor-pointer transition-all duration-300 ${
                                                selectedSession?.id === session.id 
                                                    ? 'border-primary shadow-lg ring-2 ring-primary/20' 
                                                    : 'hover:border-primary/30 hover:shadow-md'
                                            }`}
                                            onClick={() => handleSessionSelect(session)}
                                            data-testid={`session-card-${session.id}`}
                                        >
                                            <CardContent className="p-3 sm:p-4">
                                                <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                                                    <h4 className="font-semibold text-foreground text-sm min-w-0">{session.name}</h4>
                                                    <Badge variant="secondary" className="bg-primary/10 text-primary font-bold flex-shrink-0">
                                                        £{session.price.toFixed(0)}
                                                    </Badge>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" /> {session.duration_minutes >= 60 ? `${session.duration_minutes / 60}h` : `${session.duration_minutes}m`}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Users className="w-3 h-3" /> Max {session.max_participants}
                                                    </span>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </div>

                            {/* Calendar */}
                            <div data-testid="calendar-section">
                                <h3 className="text-lg font-semibold text-foreground mb-4">2. Pick a Date</h3>
                                <Card className="overflow-hidden">
                                    <CardContent className="p-4">
                                        <Calendar
                                            mode="single"
                                            selected={selectedDate}
                                            onSelect={(date) => {
                                                if (isDateBlocked(date)) {
                                                    toast.error('This date is not available for booking');
                                                    return;
                                                }
                                                setSelectedDate(date);
                                                setSelectedSlot(null);
                                            }}
                                            disabled={(date) => {
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                return date < today || !isDateAvailable(date) || isDateBlocked(date);
                                            }}
                                            modifiers={{
                                                available: (date) => isDateAvailable(date) && !isDateBlocked(date),
                                                blocked: (date) => isDateBlocked(date)
                                            }}
                                            modifiersClassNames={{
                                                available: 'bg-primary/10 text-primary font-semibold hover:bg-primary/20',
                                                blocked: 'bg-gray-200 text-gray-400 line-through cursor-not-allowed'
                                            }}
                                            className="rounded-md"
                                            data-testid="booking-calendar"
                                        />
                                        {!selectedSession && (
                                            <p className="text-sm text-muted-foreground mt-4 text-center">
                                                Select a session to see available dates
                                            </p>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Time Slots & Add to Cart */}
                            <div data-testid="slots-section">
                                <h3 className="text-lg font-semibold text-foreground mb-4">3. Choose Time & Book</h3>
                                <Card>
                                    <CardContent className="p-4">
                                        {!selectedDate ? (
                                            <div className="text-center py-8 text-muted-foreground">
                                                <CalendarDays className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                                                <p>Select a date to see available times</p>
                                            </div>
                                        ) : availableSlots.length === 0 ? (
                                            <div className="text-center py-8 text-muted-foreground">
                                                <Clock className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                                                <p>No slots available for this date</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    {availableSlots.map((slot) => {
                                                        const isFull = slot.is_full || slot.available_spots <= 0;
                                                        const isOverbooked = slot.available_spots < 0;
                                                        const isSelected = selectedSlot?.id === slot.id;
                                                        
                                                        return (
                                                            <div
                                                                key={slot.id}
                                                                className={`
                                                                    flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border transition-all
                                                                    ${isFull 
                                                                        ? 'bg-red-50 border-red-200 cursor-pointer hover:bg-red-100' 
                                                                        : isSelected 
                                                                            ? 'bg-primary text-white border-primary cursor-pointer' 
                                                                            : 'bg-white border-gray-200 hover:border-primary/50 hover:bg-primary/5 cursor-pointer'
                                                                    }
                                                                `}
                                                                onClick={() => handleSlotClick(slot)}
                                                                data-testid={`time-slot-${slot.time}`}
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <Clock className={`w-4 h-4 ${isFull ? 'text-red-500' : isSelected ? 'text-white' : 'text-primary'}`} />
                                                                    <span className={`font-medium ${isFull ? 'text-red-700' : ''}`}>{slot.time}</span>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {isOverbooked ? (
                                                                        <Badge className="bg-red-600 text-white text-xs font-bold">
                                                                            OVERBOOKED
                                                                        </Badge>
                                                                    ) : isFull ? (
                                                                        <Badge className="bg-red-500 text-white text-xs font-bold">
                                                                            FULL
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge 
                                                                            variant="secondary" 
                                                                            className={`text-xs ${isSelected ? 'bg-white/20 text-white' : 'bg-green-100 text-green-700'}`}
                                                                        >
                                                                            <Users className="w-3 h-3 mr-1" />
                                                                            {slot.available_spots} of {slot.max_capacity} spaces
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {selectedSlot && !selectedSlot.is_full && selectedSlot.available_spots > 0 && (
                                                    <div className="bg-secondary/50 rounded-xl p-4 mt-4" data-testid="booking-summary">
                                                        <h4 className="font-semibold text-foreground mb-2">Your Selection</h4>
                                                        <div className="space-y-1 text-sm text-muted-foreground">
                                                            <p><strong>Session:</strong> {selectedSession.name}</p>
                                                            <p><strong>Date:</strong> {format(selectedDate, 'EEEE, d MMMM yyyy')}</p>
                                                            <p><strong>Time:</strong> {selectedSlot.time}</p>
                                                            <p className="text-lg font-bold text-primary mt-2">
                                                                Total: £{selectedSession.price.toFixed(2)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {selectedSlot && (selectedSlot.is_full || selectedSlot.available_spots <= 0) && (
                                                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4 text-center" data-testid="session-full-message">
                                                        <p className="text-red-700 font-semibold mb-1">This session is full</p>
                                                        <p className="text-red-600 text-sm">Join the waitlist and we'll notify you if a space becomes available.</p>
                                                    </div>
                                                )}

                                                {(!selectedSlot || (selectedSlot.is_full || selectedSlot.available_spots <= 0)) ? (
                                                    <Button
                                                        className="w-full bg-red-200 text-red-700 cursor-not-allowed opacity-60 hover:bg-red-200"
                                                        disabled={true}
                                                        data-testid="add-to-cart-button"
                                                    >
                                                        {selectedSlot && (selectedSlot.is_full || selectedSlot.available_spots <= 0) 
                                                            ? 'SESSION FULL' 
                                                            : 'Select a time slot'}
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        className="w-full btn-primary"
                                                        disabled={addingToCart}
                                                        onClick={handleAddToCart}
                                                        data-testid="add-to-cart-button"
                                                    >
                                                        {addingToCart ? (
                                                            <>
                                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                Adding...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ShoppingCart className="w-4 h-4 mr-2" />
                                                                Add to Cart
                                                            </>
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    )}
                    
                    {/* Can't make a session? */}
                    <div className="mt-12 text-center max-w-xl mx-auto">
                        <h3 className="text-2xl font-bold text-foreground font-serif mb-4">
                            Can't Make a Session?
                        </h3>
                        <p className="text-muted-foreground mb-6">
                            If none of the available dates work for you, register your interest and we'll notify you when new sessions become available.
                        </p>
                        <Link to="/contact">
                            <Button className="btn-primary px-8">
                                <UserPlus className="w-4 h-4 mr-2" />
                                Register Your Interest
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Waitlist Modal */}
            {showWaitlistModal && waitlistSlot && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="waitlist-modal">
                    <Card className="w-full max-w-md">
                        <CardContent className="p-6">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-xl font-bold text-foreground font-serif">Join Waitlist</h3>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        This session is currently full
                                    </p>
                                </div>
                                <button 
                                    onClick={() => {
                                        setShowWaitlistModal(false);
                                        setWaitlistSlot(null);
                                        setWaitlistForm({ name: '', email: '', phone: '' });
                                    }}
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-amber-800">
                                        <strong>{selectedSession?.name}</strong><br />
                                        {format(selectedDate, 'EEEE, d MMMM yyyy')} at {waitlistSlot.time}
                                    </div>
                                </div>
                            </div>
                            
                            <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                                <div>
                                    <Label htmlFor="waitlist-name">Name *</Label>
                                    <Input
                                        id="waitlist-name"
                                        value={waitlistForm.name}
                                        onChange={(e) => setWaitlistForm(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Your full name"
                                        required
                                        data-testid="waitlist-name-input"
                                    />
                                </div>
                                
                                <div>
                                    <Label htmlFor="waitlist-email">Email *</Label>
                                    <Input
                                        id="waitlist-email"
                                        type="email"
                                        value={waitlistForm.email}
                                        onChange={(e) => setWaitlistForm(prev => ({ ...prev, email: e.target.value }))}
                                        placeholder="your@email.com"
                                        required
                                        data-testid="waitlist-email-input"
                                    />
                                </div>
                                
                                <div>
                                    <Label htmlFor="waitlist-phone">Phone *</Label>
                                    <Input
                                        id="waitlist-phone"
                                        type="tel"
                                        value={waitlistForm.phone}
                                        onChange={(e) => setWaitlistForm(prev => ({ ...prev, phone: e.target.value }))}
                                        placeholder="07123 456789"
                                        required
                                        data-testid="waitlist-phone-input"
                                    />
                                </div>
                                
                                <p className="text-xs text-muted-foreground">
                                    We'll contact you if a space becomes available. Your details will only be used for this purpose.
                                </p>
                                
                                <Button
                                    type="submit"
                                    className="w-full btn-primary"
                                    disabled={submittingWaitlist}
                                    data-testid="waitlist-submit-button"
                                >
                                    {submittingWaitlist ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <UserPlus className="w-4 h-4 mr-2" />
                                            Join Waitlist
                                        </>
                                    )}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default MantrailingPage;
